import {
  Button,
  Group,
  Input,
  Pill,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { useField } from '@mantine/form'
import { KeyboardEvent, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

type MultiValueFieldProps = {
  label: string
  removeLabel: string
  icon: ReactNode
  placeholder: string
  description: ReactNode
  values: string[]
  error?: ReactNode
  disabled?: boolean
  // Emails accept comma-separated input; webhook URLs may contain commas, so they do not.
  splitOnComma?: boolean
  validate: (value: string) => ReactNode
  onAdd: (value: string) => void
  onRemove: (index: number) => void
  onBlockingDraftChange?: (blocking: boolean) => void
}

export const MultiValueField = ({
  label,
  removeLabel,
  icon,
  placeholder,
  description,
  values,
  error,
  disabled,
  splitOnComma,
  validate,
  onAdd,
  onRemove,
  onBlockingDraftChange
}: MultiValueFieldProps) => {
  const field = useField({ initialValue: '', validate })

  const handleAdd = () => {
    const entries = splitOnComma
      ? field.getValue().split(',')
      : [field.getValue()]

    const trimmed = entries.map((entry) => entry.trim()).filter(Boolean)

    if (trimmed.length === 0) {
      onBlockingDraftChange?.(false)
      return
    }

    const invalid = trimmed.find((entry) => !!validate(entry))

    if (invalid) {
      field.setValue(invalid)
      void field.validate()
      onBlockingDraftChange?.(true)
      return
    }

    const seen = new Set(values)

    trimmed.forEach((entry) => {
      if (seen.has(entry)) {
        return
      }

      seen.add(entry)
      onAdd(entry)
    })

    onBlockingDraftChange?.(false)
    field.reset()
  }

  // Clicking Save blurs the input first, so committing here means a value the user
  // typed but never pressed Add on still makes it into the payload.
  const handleBlur = () => {
    if (field.getValue().trim()) {
      handleAdd()
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const isSeparator =
      event.key === 'Enter' || (splitOnComma && event.key === ',')

    if (!isSeparator) {
      return
    }

    event.preventDefault()
    handleAdd()
  }

  return (
    <Stack gap={6}>
      <Group gap={6} wrap="nowrap">
        {icon}

        <Text size="sm" fw={500}>
          {label}
        </Text>
      </Group>

      <Group gap="xs" wrap="nowrap" align="flex-start">
        <TextInput
          flex={1}
          aria-label={label}
          placeholder={placeholder}
          disabled={disabled}
          {...field.getInputProps()}
          onChange={(event) => {
            field.getInputProps().onChange(event)

            if (!event.currentTarget.value.trim()) {
              onBlockingDraftChange?.(false)
            }
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />

        <Button
          variant="light"
          miw={100}
          disabled={disabled}
          onClick={handleAdd}
        >
          <FormattedMessage id="workspace.notifications.add" />
        </Button>
      </Group>

      <Text size="xs" c="dimmed">
        {description}
      </Text>

      <Input.Wrapper error={error}>
        {values.length > 0 && (
          <Pill.Group mt="xs" mb={error ? 'xs' : 0}>
            {values.map((value, index) => (
              <Pill
                key={`${value}-${index}`}
                size="md"
                styles={(theme) => ({
                  root: { outline: `1px solid ${theme.colors.gray[4]}` }
                })}
                disabled={disabled}
                removeButtonProps={{ 'aria-label': `${removeLabel}: ${value}` }}
                withRemoveButton
                onRemove={() => onRemove(index)}
              >
                <Group gap={4} wrap="nowrap">
                  {icon}
                  <span>{value}</span>
                </Group>
              </Pill>
            ))}
          </Pill.Group>
        )}
      </Input.Wrapper>
    </Stack>
  )
}
